import { useAuth } from "@/src/contexts/auth.context";
import {
  saveRefreshToken,
  saveToken,
  setActiveOrg,
  setOrgSelected,
  setRememberMe,
  useLoginMutation,
  useSSOExchangeMutation,
} from "@/src/services/api/auth.api";
import { zodResolver } from "@hookform/resolvers/zod";
import { useLocalSearchParams, useRouter } from "expo-router";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const loginSchema = z.object({
  email: z.string().min(1, "ERROR_NO_EMAIL").email("ERROR_EMAIL_NOT_VALID"),
  password: z.string().min(1, "ERROR_NO_PASSWORD"),
  rememberMe: z.boolean().default(false),
});

export type LoginFormData = Readonly<z.input<typeof loginSchema>>;

export type SSOStatus = Readonly<{
  message: string;
  type: "success" | "error";
}>;

export const useLogin = () => {
  const router = useRouter();
  const { t } = useTranslation();
  const params = useLocalSearchParams();
  const { code, state } = params;

  const [ssoStatus, setSsoStatus] = useState<SSOStatus | null>(null);

  const form = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
      rememberMe: false,
    },
  });

  const { refreshAuth } = useAuth();

  const {
    control,
    handleSubmit,
    formState: { errors },
    setError: setFormError,
    clearErrors,
  } = form;

  const ssoExchangeMutation = useSSOExchangeMutation({
    onSuccess: async (data) => {
      if (data.access_token && data.refresh_token) {
        await saveToken(data.access_token);
        await saveRefreshToken(data.refresh_token);
        await setActiveOrg(undefined);
        await setOrgSelected(false);
        await refreshAuth();
        router.replace("/");
        loginMutation.reset();
        setSsoStatus(null);
      } else {
        const message =
          typeof data.message === "string"
            ? data.message
            : data.message?.error || t("SUCCESS_SSO_REGISTRATION_PENDING");

        setSsoStatus({
          message: message,
          type: "success",
        });
        clearErrors("email");
      }
    },

    onError: (err: any) => {
      console.error("SSO Exchange failed", err);

      const status = err.response?.status;
      const responseMessage = err.response?.data?.message;

      const errorMessage =
        typeof responseMessage === "string"
          ? responseMessage
          : responseMessage?.error_description;

      if (status === 400) {
        setFormError("email", {
          message: errorMessage || t("ERROR_SSO_VALIDATION"),
        });
        setSsoStatus(null);
      } else if (status === 401) {
        setFormError("email", {
          message: errorMessage || t("ERROR_SSO_AUTH_FAILED"),
        });
        setSsoStatus(null);
      } else if (status === 403) {
        setSsoStatus({
          message: errorMessage || t("ERROR_ACCOUNT_DEACTIVATED"),
          type: "error",
        });
        clearErrors("email");
      } else if (status === 500) {
        setFormError("email", {
          message: t("ERROR_SERVER"),
        });
        setSsoStatus(null);
      } else {
        setFormError("email", {
          message: errorMessage || t("ERROR_SSO_FAILED"),
        });
        setSsoStatus(null);
      }
    },
  });

  const { mutate: ssoExchange } = ssoExchangeMutation;

  const loginMutation = useLoginMutation({
    onSuccess: async (data) => {
      await saveToken(data.access_token!);
      await saveRefreshToken(data.refresh_token!);
      await setActiveOrg(undefined);
      await setOrgSelected(false);
      await refreshAuth();
      router.replace("/");
    },
    onError: (err: any) => {
      const status = err.response?.status;

      const responseMessage = err.response?.data?.message;

      const errorMessage =
        typeof responseMessage === "string"
          ? responseMessage
          : responseMessage?.error_description;

      if (status === "user_inactive_or_not_verified") {
        setFormError("email", {
          message: t("ERROR_ACCOUNT_INACTIVE_OR_UNVERIFIED"),
        });
        return;
      }

      if (status === 400 || status === 422) {
        setFormError("email", {
          message: errorMessage || t("ERROR_INVALID_CREDENTIALS"),
        });
      } else if (status === 403) {
        setFormError("email", {
          message: errorMessage || t("ERROR_ACCOUNT_DEACTIVATED"),
        });
      } else if (status === 401) {
        setFormError("password", {
          message: errorMessage || t("ERROR_INVALID_PASSWORD_EMAIL"),
        });
      } else if (status === 500) {
        setFormError("email", {
          message: t("ERROR_SERVER"),
        });
      } else {
        setFormError("password", {
          message: errorMessage || t("ERROR_LOGIN_FAILED"),
        });
      }
    },
  });

  const onLogin = (data: LoginFormData) => {
    setSsoStatus(null);
    setRememberMe(data.rememberMe ?? false);
    loginMutation.mutate({
      email: data.email,
      password: data.password,
      rememberMe: data.rememberMe,
    });
  };

  const handleSSOError = (error: string, status?: string) => {
    setSsoStatus({
      message: error,
      type: "error",
    });
    clearErrors("email");
  };

  useEffect(() => {
    if (code && state) {
      ssoExchange({ code: code as string, state: state as string });
    }
  }, [code, state, ssoExchange]);

  const navigateToForgotPassword = () => {
    router.push("/forgot-password");
  };

  const navigateToRegistration = () => {
    router.replace("/registration");
  };

  return {
    control,
    handleSubmit,
    errors,

    ssoStatus,
    isAuthenticating: ssoExchangeMutation.isPending,
    isLoggingIn: loginMutation.isPending,

    onLogin,
    handleSSOError,
    navigateToForgotPassword,
    navigateToRegistration,

    t,
  };
};
